import { Injectable } from '@angular/core'
import {
  ContentWithAnalytics,
  createContentListWithAnalytics,
  EventContent,
  NonEventContent
} from '../../shared/functions/analytics'
import { SegmentPageProperties, SegmentTagV2 } from '../../shared/analytics/models/segment-tag.model'
import { SegmentService } from '../../shared/analytics/services/segment.service'
import {
  DEFAULT_OG_IMAGE_HEIGHT,
  DEFAULT_OG_IMAGE_WIDTH,
  PageMetaService,
  removeTrailingSlash
} from '../../libs/page-meta/page-meta.service'
import { VerticalService } from '../../singleton-services/vertical.service'
import { take } from 'rxjs/operators'

/**
 * The standard Page tag for the article list page view.
 *
 * @remarks
 * This is expected to be used with pageV2, as indicated by the use of the
 * SegmentTagV2 interface and `autoPageCall: false`.
 *
 * @see SegmentService#pageV2
 * @see SegmentTagV2
 */
export const ARTICLE_LIST_TAG: SegmentTagV2 = {
  page_category: 'News',
  subpage_category: 'Home',
  category: 'Articles',
  autoPageCall: false
}

@Injectable({
  providedIn: 'root'
})
export class ArticleListAnalyticsService {
  public constructor(
    private readonly segment: SegmentService,
    private readonly pageMeta: PageMetaService,
    private readonly vertical: VerticalService
  ) {}

  /**
   * Add the list analytics to articles.
   *
   * @remarks
   * This Method is separated to simplify the tests around the `ArticleListComponent`.
   */
  public addListAnalytics<A extends EventContent | NonEventContent>(
    articles: ReadonlyArray<A>
  ): ReadonlyArray<ContentWithAnalytics<A>> {
    return createContentListWithAnalytics(articles)
  }

  public setPageMeta(): void {
    this.vertical.siteSettings$.pipe(take(1)).subscribe(settings => {
      const canonicalUrl = settings.core_site_url ? `${removeTrailingSlash(settings.core_site_url)}/articles` : ''
      this.pageMeta.setPageMeta({
        type: 'website',
        twitterCard: 'summary_large_image',
        title: settings.meta_articles_page_title ?? '',
        description: settings.meta_articles_page_description ?? '',
        canonicalUrl,
        siteName: settings.site_name,
        image: {
          url: settings.meta_default_image ?? '',
          height: DEFAULT_OG_IMAGE_HEIGHT,
          width: DEFAULT_OG_IMAGE_WIDTH
        }
      })
    })
  }

  /**
   * @todo Make union for segment page properties
   */
  public sendPageCall(pageProperties: SegmentPageProperties = { _tag: 'list' }): void {
    this.segment.pageV2(ARTICLE_LIST_TAG, pageProperties)
  }
}
